import { usePrompt } from '@ur/react-components'
import { useTranslate } from '@ur/react-hooks'
import { Button } from 'components'
import {
  EditResponsibleUserModal,
  Module,
  ResponsibleUserForm,
} from 'modules/companies'
import { UserProfileLink } from 'modules/users/UserProfile/UserProfileLink'
import { UserThumbOrInitials } from 'modules/users/UserThumbOrInitials'
import { useMemo } from 'react'
import styled from 'styled-components'
import { useModuleConfig } from 'util/hooks'
import { useModuleConfigMutations } from '../../util'
import { Section, SectionHeader } from '../common'

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const UserInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  & > div {
    margin-right: 1rem;
  }
  & > a {
    color: ${props => props.theme.colors.text.dark};
  }
`

export const AbsenceSettingsResponsible: React.VFC = () => {
  const translations = useTranslate({
    update: 'common.update',
    absence: 'common.absence',
    success: 'settings.toasts.company-settings-updated',
    error: 'settings.errors.company-settings-generic',
    edit: 'settings.edit-absence-responsible',
    responsibleUser: 'settings.absence-responsible',
    noAbsenceResponsible: 'settings.no-selected-absence-responsible',
  })
  const addPrompt = usePrompt()

  const { moduleConfig: absenceConfig } = useModuleConfig(Module.ABSENCES)

  const responsibleUser = useMemo(() => {
    return absenceConfig?.responsibleUser ?? null
  }, [absenceConfig])

  const { patch: patchModuleConfig } = useModuleConfigMutations()

  async function handleCreatePrompt() {
    const { data } = await addPrompt<ResponsibleUserForm | null>(resolve => (
      <EditResponsibleUserModal
        responsibleUser={responsibleUser}
        onSubmit={resolve}
      />
    ))
    if (!absenceConfig) return
    if (data === null || data.userId === null) return

    await patchModuleConfig({
      variables: {
        id: absenceConfig.id,
        input: {
          responsibleUser: data.userId,
        },
      },
    })
  }

  return (
    <Section>
      <Header>
        <SectionHeader>{translations.responsibleUser}</SectionHeader>
        <Button
          iconLeftProps={{ icon: 'edit' }}
          padding="1rem"
          height="3rem"
          onClick={handleCreatePrompt}
        >
          {translations.edit}
        </Button>
      </Header>
      {responsibleUser ? (
        <UserInfoWrapper>
          <UserThumbOrInitials
            user={responsibleUser}
            size={46}
            fontWeight={600}
          />
          <UserProfileLink user={responsibleUser} />
        </UserInfoWrapper>
      ) : (
        <div>{translations.noAbsenceResponsible}</div>
      )}
    </Section>
  )
}
